import { useRef, type ComponentProps, type FocusEvent, type ReactNode } from 'react';
import { Menu as Base } from '@base-ui/react/menu';
import { CANVAS_UI } from '../features/editor/canvas-ui';
import { cn } from './cn';
import { returnFocusUnlessMoved } from './focus';

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

export const menuPopupClass = 'os-pop min-w-44 rounded-2xl bg-chrome p-1.5 font-sans text-sm text-fg shadow-float outline-none';
export const menuItemClass =
  'flex h-9 cursor-default select-none items-center gap-2 rounded-xl px-2.5 outline-none data-[highlighted]:bg-chrome-hover data-[disabled]:opacity-50 pointer-coarse:h-11';

const firstItem = (popup: HTMLElement) => popup.querySelector<HTMLElement>('[role^="menuitem"]:not([aria-disabled="true"])');

/** Menu popup in a portal, positioned against its trigger; marked as canvas UI so the editor leaves its events alone. */
function Popup({
  label,
  children,
  side = 'bottom',
  align = 'start',
  sideOffset = 6,
  className,
  finalFocus,
  anchor,
  focusFirst = false,
}: {
  label?: string;
  children: ReactNode;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  className?: string;
  /** Where the focus goes on close (Base UI); false leaves it to what an item opened. */
  finalFocus?: ComponentProps<typeof Base.Popup>['finalFocus'];
  /** Positions it against this instead of its trigger (a point on the canvas: `pointAnchor`). */
  anchor?: ComponentProps<typeof Base.Positioner>['anchor'];
  /** Moves the focus to the first enabled item on open (a menu opened at a point, without a trigger). */
  focusFirst?: boolean;
}) {
  const popup = useRef<HTMLDivElement>(null);
  // A given function still only runs while the focus is ours: an item that focused the canvas keeps it there.
  const moved = returnFocusUnlessMoved(popup);
  const final: typeof finalFocus = typeof finalFocus === 'function' ? (type) => (moved() ? finalFocus(type) : false) : (finalFocus ?? moved);
  return (
    <Base.Portal>
      <Base.Positioner side={side} align={align} sideOffset={sideOffset} anchor={anchor} className="z-50" {...{ [CANVAS_UI]: '' }}>
        <Base.Popup
          ref={popup}
          aria-label={label}
          finalFocus={final}
          // Base UI focuses the popup itself on open: pass it on to the first enabled item.
          onFocus={focusFirst ? (e: FocusEvent<HTMLElement>) => e.target === e.currentTarget && firstItem(e.currentTarget)?.focus() : undefined}
          className={cn(menuPopupClass, className)}
        >
          {children}
        </Base.Popup>
      </Base.Positioner>
    </Base.Portal>
  );
}

function Item({ className, ...rest }: ComponentProps<typeof Base.Item>) {
  return <Base.Item className={cn(menuItemClass, className as string)} {...rest} />;
}

function SubmenuTrigger({ className, ...rest }: ComponentProps<typeof Base.SubmenuTrigger>) {
  return <Base.SubmenuTrigger className={cn(menuItemClass, 'justify-between', className as string)} {...rest} />;
}

function Separator() {
  return <div role="separator" className="-mx-1 my-1 h-px bg-border" />;
}

/** A zero-size anchor at client coordinates, for menus opened at a point (context menu, quick create). */
export function pointAnchor(x: number, y: number) {
  return { getBoundingClientRect: () => DOMRect.fromRect({ x, y, width: 0, height: 0 }) };
}

export const Menu = { Root: Base.Root, Trigger: Base.Trigger, Popup, Item, Separator, SubmenuRoot: Base.SubmenuRoot, SubmenuTrigger };
